import React, { useState, useEffect, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
	Button,
	Avatar,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	CircularProgress,
	Select,
	InputLabel,
	MenuItem,
	FormControl,
	TextField,
	InputAdornment,
	IconButton,
	FormControlLabel
  } from '@material-ui/core';

import { Visibility, VisibilityOff } from '@material-ui/icons';

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import useConnections from "../../hooks/useConnections";

const NO_USER_CONNECTION = "__no_user_connection__";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	multFieldLine: {
		display: "flex",
		"& > *:not(:last-child)": {
			marginRight: theme.spacing(1),
		},
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
	formControl: {
		margin: theme.spacing(1),
		minWidth: 120,
	},
	profilePhotoRow: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1.5),
		marginBottom: theme.spacing(1),
	},
	profilePhotoPreview: {
		width: 48,
		height: 48,
		fontSize: 20,
	},
	hiddenInput: {
		display: "none",
	},
}));

const UserSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Muy corto")
		.max(50, "Muy largo")
		.required("Requerido"),
	password: Yup.string().min(5, "Muy corto").max(50, "Muy largo"),
	email: Yup.string().email("Correo inválido").required("Requerido"),
});

const UserModal = ({ open, onClose, userId }) => {
	const classes = useStyles();

	const initialState = {
		name: "",
		email: "",
		password: "",
		profile: "user",
		profilePicUrl: "",
		allowViewPendingTickets: true,
		allowViewUnassignedTickets: true
	};

	const { user: loggedInUser } = useContext(AuthContext);
	const canAssignSuperadmin = loggedInUser.profile === "superadmin";
	const canAssignOwner = ["superadmin", "owner"].includes(loggedInUser.profile);
	const hideResellerControls = Boolean(loggedInUser?.company?.resellerBranding);

	const [user, setUser] = useState(initialState);
	const [selectedQueueIds, setSelectedQueueIds] = useState([]);
	const [showPassword, setShowPassword] = useState(false);
	const [connectionId, setConnectionId] = useState(false);
	const [profilePicFile, setProfilePicFile] = useState(null);
	const [profilePicPreview, setProfilePicPreview] = useState("");
	const {loading, connections} = useConnections();

	useEffect(() => {
		const fetchUser = async () => {
			if (!userId) return;
			try {
				const { data } = await api.get(`/users/${userId}`);
				setUser(prevState => {
					return { ...prevState, ...data };
				});
				setProfilePicPreview(data.profilePicUrl || "");
				const userQueueIds = data.queues?.map(queue => queue.id);
				setSelectedQueueIds(userQueueIds);
				setConnectionId(data.connectionId ? data.connectionId : '');
			} catch (err) {
				toastError(err);
			}
		};

		fetchUser();
	}, [userId, open]);

	const handleClose = () => {
		onClose();
		setUser(initialState);
		setProfilePicFile(null);
		setProfilePicPreview("");
	};

	const handleProfilePicChange = event => {
		const file = event.target.files?.[0];
		if (!file) return;

		setProfilePicFile(file);
		setProfilePicPreview(URL.createObjectURL(file));
	};

	const handleSaveUser = async values => {
		const userValues = { ...values };
		delete userValues.profilePicUrl;
		const userData = { ...userValues, connectionId, queueIds: selectedQueueIds };
		const requestData = profilePicFile ? new FormData() : userData;

		if (profilePicFile) {
			Object.entries(userValues).forEach(([key, value]) => {
				if (value !== undefined && value !== null) {
					requestData.append(
						key,
						typeof value === "boolean" ? String(value) : value
					);
				}
			});
			requestData.append("connectionId", connectionId || "");
			requestData.append("queueIds", JSON.stringify(selectedQueueIds));
			requestData.append("profilePic", profilePicFile);
		}

		try {
			if (userId) {
				await api.put(`/users/${userId}`, requestData);
			} else {
				await api.post("/users", requestData);
			}
			toast.success(i18n.t("userModal.success"));
		} catch (err) {
			toastError(err);
		}
		handleClose();
	};

	return (
		<div className={classes.root}>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="xs"
				fullWidth
				scroll="paper"
			>
				<DialogTitle id="form-dialog-title">
					{userId
						? `${i18n.t("userModal.title.edit")}`
						: `${i18n.t("userModal.title.add")}`}
				</DialogTitle>
				<Formik
					initialValues={user}
					enableReinitialize={true}
					validationSchema={UserSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveUser(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ touched, errors, isSubmitting, values, setFieldValue }) => (
						<Form>
							<DialogContent dividers>
								<div className={classes.profilePhotoRow}>
									<Avatar
										src={profilePicPreview}
										className={classes.profilePhotoPreview}
									>
										{user.name?.[0]}
									</Avatar>
									<input
										accept="image/*"
										className={classes.hiddenInput}
										id="profile-pic-input"
										type="file"
										onChange={handleProfilePicChange}
									/>
									<label htmlFor="profile-pic-input">
										<Button
											color="primary"
											component="span"
											size="small"
											variant="outlined"
										>
											Foto de perfil
										</Button>
									</label>
								</div>
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("userModal.form.name")}
										autoFocus
										name="name"
										error={touched.name && Boolean(errors.name)}
										helperText={touched.name && errors.name}
										variant="outlined"
										margin="dense"
										fullWidth
									/>
									<Field
										as={TextField}
										name="password"
										variant="outlined"
										margin="dense"
										label={i18n.t("userModal.form.password")}
										error={touched.password && Boolean(errors.password)}
										helperText={touched.password && errors.password}
										type={showPassword ? 'text' : 'password'}
										InputProps={{
										endAdornment: (
											<InputAdornment position="end">
											<IconButton
												aria-label="toggle password visibility"
												onClick={() => setShowPassword((e) => !e)}
											>
												{showPassword ? <VisibilityOff /> : <Visibility />}
											</IconButton>
											</InputAdornment>
										)
										}}
										fullWidth
									/>
								</div>
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("userModal.form.email")}
										name="email"
										error={touched.email && Boolean(errors.email)}
										helperText={touched.email && errors.email}
										variant="outlined"
										margin="dense"
										fullWidth
									/>
									<FormControl
										variant="outlined"
										className={classes.formControl}
										margin="dense"
									>
										<Can
											role={loggedInUser.profile}
											perform="user-modal:editProfile"
											yes={() => (
												<>
													<InputLabel id="profile-selection-input-label">
														{i18n.t("userModal.form.profile")}
													</InputLabel>

													<Field
														as={Select}
														label={i18n.t("userModal.form.profile")}
														name="profile"
														labelId="profile-selection-label"
														id="profile-selection"
														required
													>
														{canAssignSuperadmin && (
															<MenuItem value="superadmin">Superadministrador</MenuItem>
														)}
														{canAssignOwner && (
															<MenuItem value="owner">Dueño</MenuItem>
														)}
														<MenuItem value="admin">Admin</MenuItem>
														<MenuItem value="user">User</MenuItem>
													</Field>
												</>
											)}
										/>
									</FormControl>
								</div>
								{!hideResellerControls && (
									<Can
										role={loggedInUser.profile}
										perform="user-modal:editQueues"
										yes={() => (
											<QueueSelect
												selectedQueueIds={selectedQueueIds}
												onChange={values => setSelectedQueueIds(values)}
											/>
										)}
									/>
								)}
								{!hideResellerControls && (
									<Can
										role={loggedInUser.profile}
										perform="user-modal:editQueues"
										yes={() => (
											<div style={{ display: "flex", flexDirection: "column", marginTop: 6 }}>
												<FormControlLabel
													control={
														<Checkbox
															checked
															disabled
															color="primary"
														/>
													}
													label="Ver tickets asignados a el en sus areas"
												/>
												<FormControlLabel
													control={
														<Checkbox
															checked={values.allowViewPendingTickets !== false}
															color="primary"
															onChange={event =>
																setFieldValue(
																	"allowViewPendingTickets",
																	event.target.checked
																)
															}
														/>
													}
													label="Ver tickets pendientes en sus areas"
												/>
												<FormControlLabel
													control={
														<Checkbox
															checked={values.allowViewUnassignedTickets !== false}
															color="primary"
															onChange={event =>
																setFieldValue(
																	"allowViewUnassignedTickets",
																	event.target.checked
																)
															}
														/>
													}
													label="Ver tickets sin departamento"
												/>
											</div>
										)}
									/>
								)}
								{!hideResellerControls && (
									<Can
										role={loggedInUser.profile}
										perform="user-modal:editQueues"
										yes={() => (!loading &&
											<FormControl variant="outlined" margin="dense" className={classes.maxWidth} fullWidth>
												<InputLabel>{i18n.t("userModal.form.connection")}</InputLabel>
												<Field
													as={Select}
													value={connectionId || NO_USER_CONNECTION}
													onChange={(e) =>
														setConnectionId(
															e.target.value === NO_USER_CONNECTION ? "" : e.target.value
														)
													}
													label={i18n.t("userModal.form.connection")}
												>
													<MenuItem value={NO_USER_CONNECTION}>Sin conexion</MenuItem>
													{connections.map((whatsapp) => (
														<MenuItem key={whatsapp.id} value={whatsapp.id}>{whatsapp.name}</MenuItem>
													))}
												</Field>
											</FormControl>
										)}
									/>
								)}
							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("userModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{userId
										? `${i18n.t("userModal.buttons.okEdit")}`
										: `${i18n.t("userModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default UserModal;

